
import {
  IClient,
  IClientData,
  IClientParams,
  ICountry,
  IServicepoint,
  IServicepointData,
  IServicepointParams,
  ISolResponse,
  ISubclient,
  ISubclientData,
  ISubclientParams,
  IUpdateUser,
  IUpdateUserClient,
  IUpdateUserClientSubclient,
  IUpdateUserClientSubclientServicepoint,
  IUser,
  IUserDetail,
} from "@/entities";
import { IPageResponse } from "@/services";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { i18n } from "@/i18n";
import { debounce } from "lodash";
import Multiselect from "vue-multiselect";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import { SweetAlertResult } from "sweetalert2";

const SEARCH_DEBOUNCE_DELAY = 500;
const DEFAULT_PAGE_SIZE = 10;
const LARGE_PAGE_SIZE = 100;
const TOAST_AUTO_HIDE_DELAY = 4000;

@Component({
  name: "add-servicepoint-form",
  components: {
    ValidationObserver,
    ValidationProvider,
    Multiselect,
  },
})
export default class AddServicepointForm extends Vue {
  @Action("fetchClientsPage") fetchClientsPage!: (
    params: IClientParams
  ) => Promise<IPageResponse<IClientData> | undefined>;
  @Action("fetchSubclientsPage") fetchSubclientsPage!: (
    params: ISubclientParams
  ) => Promise<IPageResponse<ISubclientData> | undefined>;
  @Action("fetchServicepointsPage") fetchServicepointsPage!: (
    params: IServicepointParams
  ) => Promise<IPageResponse<IServicepointData> | undefined>;
  @Action("updateUser") updateUser!: ({
    idUser,
    updateUser,
  }: {
    idUser: number;
    updateUser: IUpdateUser;
  }) => Promise<ISolResponse | undefined>;
  @Action("addUserServicepointApproval") addUserServicepointApproval!: ({
    idUser,
    updateUser,
  }: {
    idUser: number;
    updateUser: IUpdateUser;
  }) => Promise<boolean | undefined>;

  @Getter("getLoggedUser") loggedUser: IUser | undefined;
  @Prop({ required: true }) user!: IUserDetail;
  @Prop({ required: true }) action!: string;

  $refs!: {
    form: InstanceType<typeof ValidationObserver>;
  };
  public clientsPage: IClient[] = [];
  public subclientsPage: ISubclient[] = [];
  public servicepointPage: IServicepoint[] = [];
  public clientSelected: IClient | null = null;
  public subclientSelected: ISubclient | null = null;
  public servicepointSelected: IServicepoint | null = null;
  public countrySelected: ICountry | undefined = undefined;
  public addOnlyClient = false;

  constructor() {
    super();
  }
  get titleServicepoints(): string {
    return this.isAddServicepointAction
      ? this.$t("label.assignedServicepoints").toString()
      : this.$t("label.assignedServicepointsPendingApprovals").toString();
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: i18n.t("validation.messages.entity_already_assigned").toString(),
      text: i18n.t("validation.messages.entity_already_assigned_overwrite").toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: i18n.t("label.yes").toString(),
      cancelButtonText: i18n.t("label.no").toString(),
    });
  }

  get isNotSameUserAndIsAdmin(): boolean {
    const userExists = Boolean(this.user && this.user.username);
    const isDifferentUser = userExists && this.user.username !== this.loggedUser?.username;
    const isAdmin = Boolean(this.loggedUser?.admin);
    return isDifferentUser || isAdmin;
  }

  get maxServicesPoint(): number {
    return this.user.country?.maxServicesPoint ?? 0;
  }

  get enableAddServicePoint(): boolean {
    return (
      !this.maxServicesPoint || (this.user?.servicesPointOnCashUser ?? 0) < this.maxServicesPoint
    );
  }

  async mounted(): Promise<void> {
    this.countrySelected = this.user?.country;
    await this.searchClients();
    this.rulesServicepointForm();
  }

  rulesServicepointForm(): void {
    // Crear reglas únicas por instancia para evitar conflictos
    const uniqueSuffix = this.action; // 'add-servicepoint' o 'add-servicepoint-approval'

    extend(`servicepoint_unique_${uniqueSuffix}`, {
      validate: async () => this.isServicePointUnique(),
      message: () => this.messageServicepointUnique,
    });

    extend(`entity_unique_${uniqueSuffix}`, {
      validate: async () => this.isEntityUnique(),
      message: () => this.$t("validation.messages.entity_already_assigned") as string,
    });

    extend(`servicepoint_added_${uniqueSuffix}`, {
      validate: async () => {
        if (this.clientSelected && this.addOnlyClient) {
          const clientAdded = this.user.clients?.find(
            (client) => client.id === this.clientSelected?.id
          );
          return !clientAdded?.subclients?.length;
        }
        return true;
      },
      message: () => this.$t("validation.messages.entity_already_assigned") as string,
    });
  }

  get clientOptions(): IClient[] {
    return this.clientsPage.map(this.getOptionCodeDescription);
  }
  get subclientOptions(): ISubclient[] {
    return this.subclientsPage.map(this.getOptionCodeDescription);
  }
  get servicepointOptions(): IServicepoint[] {
    return this.servicepointPage.map(this.getOptionCodeDescription);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getOptionCodeDescription(c: any): any {
    if (c.active === false) {
      c.$isDisabled = true;
    }
    if (c.codeSol) {
      c.label = c.codeSol + " - " + c.description;
    } else {
      c.label = c.description;
    }
    return c;
  }

  debouncedSearchEntidades = debounce(async (description?: string) => {
    await this.searchClients(description);
  }, SEARCH_DEBOUNCE_DELAY);

  async searchClients(description?: string): Promise<void> {
    let res;

    this.subclientSelected = null;
    this.subclientsPage = [];
    if (description) {
      res = await this.fetchClientsPage({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        countryId: this.countrySelected?.id,
        codeOrDesc: description,
      });
    } else {
      res = await this.fetchClientsPage({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        countryId: this.countrySelected?.id,
      });
    }

    if (!res) {
      this.clientsPage = [];
    } else {
      this.clientsPage = res.content;
    }
  }

  async onChangeClient(): Promise<void> {
    this.clearSubClientServicePoint();

    if (this.clientSelected) {
      await this.searchCentros();
    }
  }

  async onChangeAddOnlyClient(): Promise<void> {
    this.$refs.form.reset();
    if (this.addOnlyClient) {
      this.clearSubClientServicePoint();
    } else {
      if (this.clientSelected) {
        await this.searchCentros();
      }
    }
  }

  clearSubClientServicePoint(): void {
    this.subclientSelected = null;
    this.servicepointSelected = null;
    this.subclientsPage = [];
    this.servicepointPage = [];
  }

  debouncedSearchCentros = debounce(
    async (id?: string) => this.searchCentros(id),
    SEARCH_DEBOUNCE_DELAY
  );

  async searchCentros(description?: string): Promise<void> {
    let res;
    this.servicepointSelected = null;

    if (description) {
      res = await this.fetchSubclientsPage({
        page: 0,
        size: DEFAULT_PAGE_SIZE,
        clientId: this.clientSelected?.id,
        codeOrDesc: description,
      });
    } else {
      res = await this.fetchSubclientsPage({
        page: 0,
        size: LARGE_PAGE_SIZE,
        clientId: this.clientSelected?.id,
      });
    }

    if (!res) {
      this.subclientsPage = [];
    } else {
      this.subclientsPage = res.content;
      this.subclientsPage.unshift({
        id: -1,
        description: i18n.t("label.all").toString(),
      });
    }
  }

  async onChangeSubClient(): Promise<void> {
    this.servicepointSelected = null;
    this.servicepointPage = [];

    if (this.clientSelected) {
      await this.searchServicePoint();
    }
  }

  debouncedSearchServicePoint = debounce(
    async (description?: string) => this.searchServicePoint(description),
    SEARCH_DEBOUNCE_DELAY
  );

  async searchServicePoint(description?: string): Promise<void> {
    let res;

    if (this.subclientSelected?.id !== -1) {
      if (description) {
        res = await this.fetchServicepointsPage({
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          subclientId: this.subclientSelected?.id,
          active: true,
          codeOrDesc: description,
        });
      } else {
        res = await this.fetchServicepointsPage({
          page: 0,
          size: LARGE_PAGE_SIZE,
          subclientId: this.subclientSelected?.id,
          active: true,
        });
      }

      if (!res) {
        this.servicepointPage = [];
      } else {
        this.servicepointPage = res.content;
        this.servicepointPage.unshift({
          id: -1,
          description: i18n.t("label.all").toString(),
        });
      }
    }
  }

  async onSubmit(): Promise<void> {
    if (this.user.id) {
      const updateUser: IUpdateUser = {
        addOnlyClient: this.addOnlyClient,
        numberOfServicesPoint: this.user.servicesPointOnCashUser ?? 0,
        clients: [],
      };

      const updateServicepointSelected: IUpdateUserClientSubclientServicepoint = {
        id: this.servicepointSelected?.id ?? -1,
      };

      const updateSubclientSelected: IUpdateUserClientSubclient = {
        id: this.subclientSelected?.id ?? -1,
        servicepoints: [],
      };

      if (updateServicepointSelected.id !== -1) {
        updateSubclientSelected.servicepoints.push({ id: updateServicepointSelected.id });
      }

      const updateClientSelected: IUpdateUserClient = {
        id: this.clientSelected?.id ?? -1,
        subclients: [],
      };

      if (updateSubclientSelected.id !== -1) {
        updateClientSelected.subclients?.push(updateSubclientSelected);
      }

      let clientAdded = this.user.clients?.find((client) => client.id === updateClientSelected.id);
      if (clientAdded && !this.addOnlyClient && !clientAdded?.subclients?.length) {
        const { isConfirmed } = await this.modalConfirm();
        if (!isConfirmed) {
          return;
        }
      }
      updateUser.clients.push(updateClientSelected);

      await this.fetchUpdateUser(updateUser);
    }

    this.$refs.form.reset();
  }

  async fetchUpdateUser(updateUser: IUpdateUser): Promise<void> {
    if (this.isAddServicepointAction) {
      let response = await this.updateUser({ idUser: this.user.id!, updateUser });
      this.subclientSelected = null;
      this.servicepointSelected = null;

      if (this.addOnlyClient) {
        this.clientSelected = null;
      }

      if (response?.updatedSmartcash === undefined || response?.updatedSmartcash) {
        this.makeToast("success", i18n.t("validation.messages.user.ok").toString());
      } else {
        this.makeToast("warning", i18n.t("validation.messages.user.ko").toString());
      }
    } else {
      let response = await this.addUserServicepointApproval({
        idUser: this.user.id!,
        updateUser,
      });
      this.subclientSelected = null;
      this.servicepointSelected = null;

      if (this.addOnlyClient) {
        this.clientSelected = null;
      }

      if (response) {
        this.makeToast("success", i18n.t("validation.messages.user.ok").toString());
      } else {
        this.makeToast("warning", i18n.t("validation.messages.user.ko").toString());
      }
    }
    this.$emit("change", this.user.id);
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: TOAST_AUTO_HIDE_DELAY,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  private isEntityUnique(): boolean {
    if (this.clientSelected && this.addOnlyClient) {
      const clientAdded = this.user.clients?.find(
        (client) => client.id === this.clientSelected?.id
      );
      return !(clientAdded && !clientAdded?.subclients?.length);
    }
    return true;
  }

  get collapseId(): string {
    return `${this.action}-form-collapse`;
  }

  get openButtonId(): string {
    return `open-${this.action}-form-btn`;
  }

  get openButtonWrapperId(): string {
    return `open-${this.action}-form-wrapper`;
  }

  get closeButtonId(): string {
    return `close-${this.action}-form-btn`;
  }

  get componentId(): string {
    return `add-servicepoint-form-${this.action}`;
  }

  get clientValidationRules(): string {
    const uniqueSuffix = this.action;
    return `required|entity_unique_${uniqueSuffix}|servicepoint_added_${uniqueSuffix}`;
  }

  get servicepointValidationRules(): string {
    const uniqueSuffix = this.action;
    return `required|servicepoint_unique_${uniqueSuffix}`;
  }

  get messageServicepointUnique(): string {
    if (!this.servicepointSelected || !this.clientSelected || !this.subclientSelected) {
      return this.$t("validation.messages.servicepoint_already_assigned");
    }

    const existsInClients = this.findServicepointInClients();
    const existsInApprovals = this.findServicepointInApprovals();

    if (this.isAddServicepointAction) {
      // Para formulario de asignados
      if (existsInClients) {
        return this.messageOnValidationServicepoint;
      } else if (existsInApprovals) {
        return this.$t("validation.messages.servicepoint_already_assigned_on_approvals");
      }
    } else {
      // Para formulario de aprobaciones
      if (existsInApprovals) {
        return this.$t("validation.messages.servicepoint_already_assigned_on_approvals");
      } else if (existsInClients) {
        return this.messageOnValidationServicepoint;
      }
    }

    return this.$t("validation.messages.servicepoint_already_assigned");
  }

  isServicePointUnique(): boolean {
    if (!this.servicepointSelected || !this.clientSelected || !this.subclientSelected) {
      return true;
    }

    if (this.isAddServicepointAction) {
      const existsInClients = this.findServicepointInClients();
      const existsInApprovals = this.findServicepointInApprovals();
      return !(existsInClients || existsInApprovals);
    } else {
      const existsInApprovals = this.findServicepointInApprovals();
      const existsInClients = this.findServicepointInClients();
      return !(existsInApprovals || existsInClients);
    }
  }

  private findServicepointInClients(): boolean {
    const clientOnUser = this.user.clients?.find((client) => client.id === this.clientSelected?.id);
    if (clientOnUser?.subclients?.length) {
      const subclientOnUser = clientOnUser.subclients.find(
        (subclientAdded) => subclientAdded.id === this.subclientSelected?.id
      );
      if (subclientOnUser) {
        return !!subclientOnUser.servicepoints?.find(
          (servicepoint) => servicepoint.id === this.servicepointSelected?.id
        );
      }
    }
    return false;
  }

  private findServicepointInApprovals(): boolean {
    const clientOnApproval = this.user.approvals?.find(
      (client) => client.id === this.clientSelected?.id
    );
    if (clientOnApproval?.subclients?.length) {
      const subclientOnApproval = clientOnApproval.subclients.find(
        (subclientAdded) => subclientAdded.id === this.subclientSelected?.id
      );
      if (subclientOnApproval) {
        return !!subclientOnApproval.servicepoints?.find(
          (servicepoint) => servicepoint.id === this.servicepointSelected?.id
        );
      }
    }
    return false;
  }

  get isAddServicepointAction(): boolean {
    return this.action === "add-servicepoint";
  }

  beforeDestroy(): void {
    // Limpiar reglas de validación específicas de esta instancia
    // Note: VeeValidate no expone un método público para eliminar reglas,
    // pero al usar nombres únicos evitamos conflictos entre instancias
  }

  get messageOnValidationServicepoint(): string {
    if (this.user.approver) {
      return this.$t("validation.messages.servicepoint_already_assigned_on_visibility");
    } else {
      return this.$t("validation.messages.servicepoint_already_assigned");
    }
  }
}
